import _ from 'lodash'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'
import { TransitionStatus } from 'react-transition-group/Transition'
import styled from 'styled-components'

import { useAppConfig } from '../../../hooks/useAppConfig'
import { Button } from '../../Button'
import { Flex } from '../../Flex'
import { Image } from '../../Image'
import { Text } from '../../Text'
import { FlagIcon, flagMap, FlagMapKeys } from '../Flags'

import { NavigationLink } from '../NavMenu'

import { ThemeWrapper } from '../../theme'

import { MobileMenuDropdown } from './MobileMenuDropdown'
import { MobileMenuItem } from './MobileMenuItem'

const MenuContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  transition: all 250ms ease-out;
  transform: translateX(-100%);

  width: 60%;
  height: 100%;
  z-index: 5000;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.primaryDark};

  @media only screen and (max-width: 400px) {
    width: 100%;
  }

  &.exited {
    display: none;
  }

  &.entered {
    transform: translateX(0);
  }
`

const StyledHeader = styled(Flex)`
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.primary};
  align-items: center;
  height: 60px;
`

const Brand = styled(Image)`
  display: block;
  height: 30px;
  margin-left: 16px;
`

const MenuItemWrapper = styled.div`
  display: flex;
  flex-direction: column;

  a {
    display: block;
    flex: 1 1 auto;
  }
`

const LangDropdownItemWrapper = styled.div`
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  user-select: none;
`

const TextWithIcon = styled(Text)`
  cursor: pointer;
  img {
    margin-inline-start: 8px;
    margin-inline-end: 8px;
  }
`

type Props = {
  visible: boolean
  setMenuVisibility: (visible: boolean) => void
  state: TransitionStatus
  changeLanguage: (lng: string) => void
  loggedIn: boolean
  fullName: string
  navigationLinks: NavigationLink[]
  footerLinks: {
    url: string
    text: string
    target?: string
  }[]
  logoImg: {
    url: string
    alt?: string
  }
  loginUrl: string
  showBulkImportMenuInHeader: boolean | undefined
  logout: () => void
  links: {
    Archive: React.FunctionComponent
    Profile: React.FunctionComponent
    Bulk: React.FunctionComponent
    Home: React.FunctionComponent
  }
}

const MobileMenuComponent = ({
  setMenuVisibility,
  state,
  changeLanguage,
  loggedIn,
  fullName,
  navigationLinks,
  footerLinks,
  logoImg,
  loginUrl,
  showBulkImportMenuInHeader,
  logout,
  links
}: Props) => {
  const { Archive: ArchiveLink, Bulk: BulkLink, Home: HomeLink, Profile: ProfileLink } = links

  const ref = useRef<any>()
  const { t, i18n } = useTranslation('core')
  const { availableLanguages } = useAppConfig()
  const [selectedMenuItem, setSelectedMenuItem] = useState<
    | {
        header: string
        links: {
          url: string
          text: string
          target?: string
        }[]
      }
    | undefined
  >(undefined)

  return (
    <MenuContainer className={state} ref={ref}>
      <StyledHeader>
        <HomeLink>
          <Brand src={logoImg.url} alt={logoImg.alt} />
        </HomeLink>
        <Button variant="transparent" onClick={() => setMenuVisibility(false)}>
          <Image type="closeWhite" alt="close" />
        </Button>
      </StyledHeader>

      <MenuItemWrapper>
        {_.size(availableLanguages) > 1 && (
          <MobileMenuDropdown title={t(`core:Languages.${i18n.language}`)} variant="secondary">
            {_.map(availableLanguages, l => (
              <LangDropdownItemWrapper
                onClick={() => {
                  changeLanguage(l)
                }}
                key={l}
              >
                <Flex alignItems="center">
                  <FlagIcon active={i18n.language === l} type={flagMap[l as FlagMapKeys]} />
                  <Text color="primary" ml={1}>
                    {t(`core:Languages.${l}`)}
                  </Text>
                </Flex>
              </LangDropdownItemWrapper>
            ))}
          </MobileMenuDropdown>
        )}

        {!loggedIn && (
          <a href={loginUrl}>
            <MobileMenuItem text={t('account:Menu.Login')} icon="personPrimary" type="secondary" />
          </a>
        )}
        {loggedIn && (
          <MobileMenuItem type="secondary">
            <Flex width="100%" justifyContent="space-between">
              <Text color="primary" bold={false}>
                {fullName}
              </Text>
              <TextWithIcon color="primary" bold onClick={logout}>
                {t('account:Menu.Logout')}
                <Image type="logOut" alt="log out" />
              </TextWithIcon>
            </Flex>
          </MobileMenuItem>
        )}

        {loggedIn && !selectedMenuItem && (
          <>
            <ArchiveLink>
              <MobileMenuItem text={t('archive:Archive')} type="primaryDark" />
            </ArchiveLink>
            <ProfileLink>
              <MobileMenuItem text={t(`account:Menu.Profile`)} type="primaryDark" />
            </ProfileLink>
            {showBulkImportMenuInHeader && (
              <BulkLink>
                <MobileMenuItem text={t(`import:BulkImport`)} type="primaryDark" />
              </BulkLink>
            )}
          </>
        )}

        {!selectedMenuItem &&
          _.map(navigationLinks, l => (
            <React.Fragment key={l.linkModel?.text || l.dropdownLinksMenuModel?.header}>
              {l.linkModel && (
                <a href={l.linkModel?.url}>
                  <MobileMenuItem text={l.linkModel?.text} />
                </a>
              )}
              {l.dropdownLinksMenuModel && (
                <>
                  <MobileMenuItem
                    onClick={() => {
                      setSelectedMenuItem(l.dropdownLinksMenuModel)
                    }}
                    isLink
                  >
                    <Flex width="100%" justifyContent="space-between">
                      <Text bold={true} color="white">
                        {l.dropdownLinksMenuModel.header}
                      </Text>
                      <Image type="arrowRightWhite" alt="arrow right" />
                    </Flex>
                  </MobileMenuItem>
                </>
              )}
            </React.Fragment>
          ))}

        {selectedMenuItem && (
          <>
            <MobileMenuItem
              type="primaryDark"
              onClick={() => {
                setSelectedMenuItem(undefined)
              }}
              isLink
            >
              <Flex width="100%">
                <Image type="arrowLeftWhite" alt="arrow left" />
                <Text bold={true} color="white" ml={1}>
                  {t('core:Back')}
                </Text>
              </Flex>
            </MobileMenuItem>
            <MobileMenuItem text={selectedMenuItem.header} separator={true} />
            {_.map(selectedMenuItem.links, dl => (
              <a href={dl.url} key={dl.text}>
                <MobileMenuItem text={dl.text} />
              </a>
            ))}
          </>
        )}

        {_.map(footerLinks, l => (
          <a href={l.url} key={l.url}>
            <MobileMenuItem text={l.text} type="transparent" justifyContent="center" />
          </a>
        ))}
      </MenuItemWrapper>
    </MenuContainer>
  )
}

export const MobileMenu = ({ visible, ...rest }: Omit<Props, 'state'>) => {
  return (
    <ThemeWrapper>
      <Transition in={visible} timeout={{ appear: 0, enter: 200, exit: 200 }} mountOnEnter unmountOnExit>
        {state => <MobileMenuComponent visible={visible} state={state} {...rest} />}
      </Transition>
    </ThemeWrapper>
  )
}

export default MobileMenu
